import {noop} from '@benchling/js-utils';
import produce from 'immer';
import React from 'react';
import _ from 'underscore';

import * as Analytics from 'analytics/Analytics';
import * as ArchiveConstants from 'archive/ArchiveConstants';
import {ArchiveOptions, ArchiveRecord, ItemForArchive} from 'archive/ArchiveConstants';
import * as ArchiveDAO from 'archive/ArchiveDAO';
import * as ArchivePurposesUtil from 'archive/ArchivePurposesUtil';
import ArchiveSequencesOption from 'archive/components/modals/ArchiveSequencesOption';
import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import {NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import * as Context from 'Context';
import {
  canProceedWithSamples,
  checkForSampleStatusWarnings,
} from 'inventory/sample-status/SampleStatusWarning';
import SampleStatusWarningBannerStack from 'inventory/sample-status/SampleStatusWarningBannerStack';
import {SampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import notifications from 'notifications';
import TestOrdersLinkedToContainersArchiveBanner from 'samples/containers/TestOrdersLinkedToContainersArchiveBanner';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {GENERIC_SHORT_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import WarningIcon from 'ui-library/icons/WarningIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';

// @ts-expect-error
import ArchiveRegisteredBioentitiesOption from './ArchiveRegisteredBioentitiesOption';
import ArchiveStorablesOption from './ArchiveStorablesOption';
import ArchiveTable, {CheckableArchiveTable} from './ArchiveTable';

const OPTION_CLASSES = [ArchiveRegisteredBioentitiesOption, ArchiveStorablesOption];

export type ArchiveOptionsForModal = ArchiveOptions & {
  parentFolderIds?: Array<string>;
  hasRegisteredItems?: boolean;
};

export interface ArchiveModalItem {
  archiveRecord?: ArchiveRecord | null;
  id: string;
  name: string;
  iconId?: string;
  projectFolder?: StringIDFolder | NumberIDFolder | null;
  // Whether the item is registered. Legacy code will try to use other item attributes to determine if the
  // item is registered (to determine whether data mods or other special-case archive handling is needed), but
  // this prop will give a direct answer.
  isRegistered?: boolean;
  readableItemType?: string;
}

export interface ArchiveModalRenderTableOverrideArgs {
  shouldShowStatusWarning: boolean;
  samples: Array<SampleStatusItem> | null;
  hasHiddenDescendantItems: boolean;
  trackingParams: SampleStatusWarningTrackingParams;
  itemIdsToSubmit: Array<string>;
  setItemIdsToSubmit: (selectedSampleIds: Array<string>) => void;
  shouldProceed: boolean;
  setShouldProceed: (shouldProceed: boolean) => void;
  showItemCheckboxes?: boolean;
}

export interface ArchiveModalPropsWithoutCloseCallback {
  items: Array<ArchiveModalItem>;
  initialPurpose?: string | null;
  onArchive: (itemIds: Array<string>, purpose: string, options: ArchiveOptionsForModal) => void;
  shouldDetectShortcuts?: boolean;
  // Lets users choose which items to archive via checkboxes. For now, when this option is enabled,
  // descendant items, which will be archived implicitly if the parent item is checked, are not listed.
  // This is because the UX around checking and unchecking descendant items can get complicated in cases
  // where they appear more than once in the list. But we are planning to implement this eventually.
  // TODO(BNCH-41283)
  showItemCheckboxes?: boolean;
  analyticsMetadata?: AnalyticsMetadataForContext;
  checkForSampleStatusWarningsParamOverride?: {[k: string]: unknown};
  renderArchivableItemTableOverride?: (args: ArchiveModalRenderTableOverrideArgs) => JSX.Element | null;
}

export interface ArchiveModalProps extends ArchiveModalPropsWithoutCloseCallback {
  onRequestClose: () => void;
}

interface State {
  error: string | null;
  itemsForArchive: Array<ItemForArchive> | null;
  purpose: string;
  options: ArchiveOptionsForModal;
  itemIdsToSubmit: Array<string>;
  shouldShowStatusWarning: boolean;
  canProceedOnSampleStatus: boolean;
  statusWarningsLoading: boolean;
  samples: Array<SampleStatusItem> | null;
}

/*
 * Archive modal component, which should be used for all archiving cases. Renders additional archiving
 * options conditionally on the items being archived.
 */
export default class ArchiveModal extends React.Component<ArchiveModalProps, State> {
  static defaultProps: Partial<ArchiveModalProps> = {
    shouldDetectShortcuts: false,
  };
  unarchivedItemIds: Array<string>;
  constructor(props: ArchiveModalProps) {
    super(props);

    this.unarchivedItemIds = [
      ...new Set(this.props.items.filter((item) => !isArchived(item)).map(({id}) => id)),
    ];

    const validPurposes = ArchivePurposesUtil.getValidArchivePurposes(this.unarchivedItemIds);
    this.state = {
      error: null,
      itemsForArchive: null,
      purpose:
        props.initialPurpose || (validPurposes.length && validPurposes[0]) || ArchiveConstants.OTHER_PURPOSE,
      options: {
        ...ArchiveRegisteredBioentitiesOption.getDefaultState(props.items),
        ...ArchiveStorablesOption.getDefaultState(),
      },
      itemIdsToSubmit: this.unarchivedItemIds,
      shouldShowStatusWarning: false,
      canProceedOnSampleStatus: false,
      statusWarningsLoading: true,
      samples: null,
    };
  }

  getSampleStatusWarningTrackingParams = (): SampleStatusWarningTrackingParams => ({
    actionAttempted: 'archive',
    actionOrigin: this.props.analyticsMetadata?.actionOrigin ?? ActionOrigin.UI_UNDETERMINED,
    sampleIds: this.state.itemsForArchive?.map(({id}) => id) ?? this.state.itemIdsToSubmit,
    eventGroupId: this.props.analyticsMetadata?.eventGroupId ?? Analytics.makeAnalyticsEventId(),
  });

  async checkForWarnings(itemsForArchive: Array<ItemForArchive>): Promise<void> {
    const {shouldShowWarning, samples} = await checkForSampleStatusWarnings({
      sampleIds: itemsForArchive.map(({id}) => id),
      ...this.props.checkForSampleStatusWarningsParamOverride,
    });
    this.setState({
      shouldShowStatusWarning: shouldShowWarning,
      statusWarningsLoading: false,
      samples: samples ?? null,
    });
  }

  override async componentDidMount(): Promise<void> {
    try {
      const response = await ArchiveDAO.getItemsForArchive(this.unarchivedItemIds, {
        shouldDetectShortcuts: this.props.shouldDetectShortcuts,
      });
      const {itemsForArchive} = response!;
      this.checkForWarnings(itemsForArchive);
      const hasRegisteredItems = itemsForArchive.some((item) => item.isRegistered);
      this.setState({itemsForArchive});
      this.handleChangeOptions({hasRegisteredItems});
      if (this.enableArchivingResultsAndHasResult(itemsForArchive)) {
        this.handleChangeOptions({resultPurpose: BenchlingConfig.ARCHIVE_RUN_AND_RESULT_PURPOSES[0]});
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      this.setState({error: notifications.getFriendlyError(e) ?? GENERIC_SHORT_ERROR_MESSAGE});
    }
  }

  private enableArchivingResultsAndHasResult(itemsForArchive: Array<ItemForArchive>): boolean {
    return (
      itemsForArchive.some((item) => item.friendlyTypeName === 'Result') &&
      (BenchlingConfig.ELN_ENABLE_ARCHIVING_RESULTS_WITH_ENTRY ||
        itemsForArchive.some((item) => item.friendlyTypeName === 'Analysis'))
    );
  }

  private setCanProceedOnSampleStatus = (canProceedOnSampleStatus: boolean): void => {
    this.setState({canProceedOnSampleStatus});
  };

  private handleChangePurpose(purpose: string): void {
    this.setState({purpose});
  }

  private handleSubmit(): void {
    for (const OptionClass of OPTION_CLASSES) {
      if (OptionClass.getValidationError && OptionClass.isEnabled(this.state.itemsForArchive)) {
        const validationError = OptionClass.getValidationError(this.state.options);
        if (validationError) {
          this.setState({error: validationError});
          return;
        }
      }
    }

    Context.usingAsync({analyticsMetadata: this.props.analyticsMetadata}, null, () => {
      this.props.onArchive(this.state.itemIdsToSubmit, this.state.purpose, this.state.options);
    });

    this.props.onRequestClose();
  }

  private handleChangeOptions(options: Partial<ArchiveOptionsForModal>): void {
    this.setState({options: {...this.state.options, ...options}});
  }

  private getArchiveReasonOptions(
    visiblePurposes: Array<string>,
    validPurposes: Array<string>
  ): Array<{name: string; value: string; disabled: boolean}> {
    return visiblePurposes.map((purpose) => ({
      name: purpose,
      value: purpose,
      disabled: !validPurposes.includes(purpose),
    }));
  }

  private getResultArchiveReasonOptions(): Array<OptionData> {
    const resultPurposeOptions: Array<string> = BenchlingConfig.ARCHIVE_RUN_AND_RESULT_PURPOSES;
    return resultPurposeOptions.map((purpose: string) => ({name: purpose, value: purpose}));
  }

  private getTestOrdersLinkedToContainersArchiveBanner(): JSX.Element | null {
    if (BenchlingConfig.SAMPLE_TESTS_ENABLE) {
      const containerIds =
        this.state.itemsForArchive?.map(({id}) => id).filter((id) => APIUtil.isContainer(id)) ?? [];

      return containerIds.length > 0 ? (
        <TestOrdersLinkedToContainersArchiveBanner containerIds={containerIds} />
      ) : null;
    }
    return null;
  }

  private createModal = (children?: JSX.Element): JSX.Element => (
    <Modal
      buttons={[
        {
          text: 'Archive',
          style: ButtonStyle.NEGATIVE,
          submit: true,
          disabled:
            this.state.itemsForArchive == null ||
            this.state.statusWarningsLoading ||
            !canProceedWithSamples(
              this.state.itemIdsToSubmit.filter((id) => APIUtil.isStorable(id)),
              this.state.shouldShowStatusWarning,
              this.state.canProceedOnSampleStatus
            ),
          dataTestAttrs: {component: 'ArchiveModal', element: 'archiveButton'},
        },
      ]}
      onRequestClose={this.props.onRequestClose}
      onRequestSubmit={() => {
        this.handleSubmit();
      }}
      title="Archive items"
      dataTestAttrs={{component: 'ArchiveModal', element: 'root'}}
      expandHeight={true}
    >
      {children}
    </Modal>
  );

  private renderTable = (): JSX.Element | null => {
    if (!this.state.itemsForArchive || this.state.statusWarningsLoading) {
      return null;
    }
    if (this.props.renderArchivableItemTableOverride) {
      // Do not show descendant items to be archived if user can select items to archive
      const samples = this.props.showItemCheckboxes
        ? (this.state.samples?.filter(({id}) => this.unarchivedItemIds.includes(id)) ?? null)
        : this.state.samples;
      return this.props.renderArchivableItemTableOverride({
        shouldShowStatusWarning: this.state.shouldShowStatusWarning,
        samples,
        hasHiddenDescendantItems: (this.state.samples?.length ?? 0) > (samples?.length ?? 0),
        trackingParams: this.getSampleStatusWarningTrackingParams(),
        itemIdsToSubmit:
          this.props.showItemCheckboxes === false
            ? (samples?.map(({id}) => id) ?? [])
            : this.state.itemIdsToSubmit,
        setItemIdsToSubmit: (selectedSampleIds) => {
          this.setState({itemIdsToSubmit: selectedSampleIds});
        },
        shouldProceed: this.state.canProceedOnSampleStatus,
        setShouldProceed: this.setCanProceedOnSampleStatus,
        showItemCheckboxes: this.props.showItemCheckboxes,
      });
    }

    return (
      <div>
        {this.state.shouldShowStatusWarning && this.state.samples && (
          <Row>
            <Col md={12}>
              <SampleStatusWarningBannerStack
                trackingParamsWithSampleIds={this.getSampleStatusWarningTrackingParams()}
                canDeselectSamples={false}
                shouldProceed={this.state.canProceedOnSampleStatus}
                onChangeShouldProceed={this.setCanProceedOnSampleStatus}
                samples={this.state.samples}
                selectedSampleIds={this.state.samples.map(({id}) => id)}
                setSelectedSampleIds={noop}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={12}>
            <p>The following items will be archived:</p>
            {this.props.showItemCheckboxes ? (
              <CheckableArchiveTable
                itemsForArchive={this.state.itemsForArchive.filter(({id}) =>
                  this.unarchivedItemIds.includes(id)
                )}
                checkedRowIds={this.state.itemIdsToSubmit}
                onChangeChecks={(newCheckedRowIds) => {
                  this.setState({itemIdsToSubmit: newCheckedRowIds});
                }}
                options={this.state.options}
              />
            ) : (
              <ArchiveTable itemsForArchive={this.state.itemsForArchive} options={this.state.options} />
            )}
          </Col>
        </Row>
      </div>
    );
  };

  override render(): JSX.Element {
    const visiblePurposes = ArchivePurposesUtil.getVisibleArchivePurposes(this.unarchivedItemIds);
    const validPurposes = ArchivePurposesUtil.getValidArchivePurposes(this.unarchivedItemIds);

    const possibleError =
      this.state.error != null ? (
        <div className="u-small-spacing-top">
          <InlineBanner bannerType={InlineBannerType.ERROR} text={this.state.error} />
        </div>
      ) : null;

    const testOrdersLinkedtoContainersArchiveBanner = this.getTestOrdersLinkedToContainersArchiveBanner();

    if (this.state.itemsForArchive && !this.state.statusWarningsLoading) {
      return this.createModal(
        <>
          {this.state.itemsForArchive.some((item) => item.friendlyTypeName === 'Result') &&
            this.state.itemsForArchive.some((item) => item.friendlyTypeName === 'Entry') && (
              <Row>
                <Col md={12}>
                  <InlineBanner
                    bannerType={InlineBannerType.INFO}
                    text="All results in the archived entries will also be archived."
                  />
                </Col>
              </Row>
            )}
          {testOrdersLinkedtoContainersArchiveBanner && (
            <Row>
              <Col md={12}>{testOrdersLinkedtoContainersArchiveBanner}</Col>
            </Row>
          )}
          {this.renderTable()}
          {visiblePurposes.length > 0 && (
            <div>
              <div className="u-spacing-top">
                <Row>
                  <Col md={12}>
                    <FieldLabel label="Mark as" />
                  </Col>
                </Row>
                <Row className="u-vertical-center-items">
                  <Col md={12}>
                    <SelectDropdown
                      displayName={this.state.purpose}
                      menu={
                        <SearchableMenu
                          height={Menu.HEIGHTS.MEDIUM}
                          width={Menu.WIDTHS.FULL}
                          selectedValue={this.state.purpose}
                          onSelect={(value: string) => {
                            this.handleChangePurpose(value);
                          }}
                          optionDatas={this.getArchiveReasonOptions(visiblePurposes, validPurposes)}
                        />
                      }
                    />
                    {_.difference(visiblePurposes, validPurposes).length > 0 && (
                      <div className="u-small-spacing-top">
                        <WarningIcon className="u-mini-spacing-right" />
                        Some archive reasons are not applicable to all selected items.
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
              {this.enableArchivingResultsAndHasResult(this.state.itemsForArchive) && (
                <div className="u-spacing-top">
                  <Row>
                    <Col md={12}>
                      <FieldLabel label="Result archive reason" />
                    </Col>
                  </Row>
                  <Row className="u-vertical-center-items">
                    <Col md={12}>
                      <SelectDropdown
                        displayName={this.state.options.resultPurpose}
                        menu={
                          <SearchableMenu
                            height={Menu.HEIGHTS.MEDIUM}
                            width={Menu.WIDTHS.FULL}
                            selectedValue={this.state.options.resultPurpose}
                            onSelect={(value: string) => {
                              this.handleChangeOptions({resultPurpose: value});
                            }}
                            optionDatas={this.getResultArchiveReasonOptions()}
                          />
                        }
                      />
                      {_.difference(visiblePurposes, validPurposes).length > 0 && (
                        <div className="u-small-spacing-top">
                          <WarningIcon className="u-mini-spacing-right" />
                          Some archive reasons are not applicable to all selected items.
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          )}
          {ArchiveRegisteredBioentitiesOption.isEnabled(this.state.itemsForArchive) && (
            <ArchiveRegisteredBioentitiesOption
              itemsForArchive={this.state.itemsForArchive}
              onChange={(options: Partial<ArchiveOptionsForModal>) => {
                this.handleChangeOptions(options);
              }}
              shouldRemoveShortcuts={this.state.options.shouldRemoveShortcuts}
              shouldUnregisterBioentities={this.state.options.shouldUnregisterBioentities}
              unregisteredBioentitiesFolderId={this.state.options.unregisteredBioentitiesFolderId}
            />
          )}
          {ArchiveStorablesOption.isEnabled(this.state.itemsForArchive) && (
            <ArchiveStorablesOption
              items={this.state.itemsForArchive}
              onChange={(options: Partial<ArchiveOptionsForModal>) => {
                this.handleChangeOptions(options);
              }}
              shouldRemoveBarcodes={this.state.options.shouldRemoveBarcodes}
              shouldArchiveContainedSamples={Boolean(this.state.options.shouldArchiveContainedSamples)}
            />
          )}
          {ArchiveSequencesOption.isEnabled(this.state.itemsForArchive) && (
            <ArchiveSequencesOption
              shouldUnlinkParts={this.state.options.shouldUnlinkParts}
              updateShouldUnlinkParts={(shouldUnlinkParts: boolean) => {
                this.handleChangeOptions(
                  produce(this.state.options, (draft) => {
                    draft.shouldUnlinkParts = shouldUnlinkParts;
                  })
                );
              }}
            />
          )}
          {possibleError}
        </>
      );
    }

    if (this.state.error == null) {
      return this.createModal(<CenteredHorizontalThrobber />);
    } else {
      return this.createModal(possibleError ?? undefined);
    }
  }
}

export function close(): void {
  ModalContainer.close(ArchiveModal);
}

export function open(props: ArchiveModalPropsWithoutCloseCallback): void {
  ModalContainer.open(
    ArchiveModal,
    <ArchiveModal
      {...props}
      onRequestClose={() => {
        close();
      }}
    />
  );
}
